import { motion } from "framer-motion";
import { Gauge, User, Users, Building2, ArrowUpRight } from "lucide-react";

// Editable service offerings
const SERVICES = [
  {
    icon: Gauge,
    title: "Free Speed Evaluation",
    tag: "Start Here",
    points: ["Movement assessment", "Sprint mechanics analysis", "Goal setting", "Performance recommendations"],
    cta: "Book Free Evaluation",
    target: "#assessment",
    featured: false,
  },
  {
    icon: User,
    title: "Private Performance Training",
    tag: "1-on-1",
    points: ["Premium individualized coaching", "Maximum attention & feedback", "Fully customized programming", "Fastest path to results"],
    cta: "Start Training",
    target: "#assessment",
    featured: false,
  },
  {
    icon: Users,
    title: "Small Group Training",
    tag: "Most Popular",
    points: ["Groups of 2–6 athletes", "Competitive environment", "Premium coaching, shared cost", "Built-in accountability"],
    cta: "Start Training",
    target: "#assessment",
    featured: true,
  },
  {
    icon: Building2,
    title: "Team Training",
    tag: "Schools · Clubs · Orgs",
    points: ["Speed & agility", "Power & sprint mechanics", "Conditioning", "Fully customized to your program"],
    cta: "Request Team Training",
    target: "#contact",
    featured: false,
  },
];

export default function Services() {
  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36 bg-[#0a0a0a] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-6"
        >
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            What We Offer
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Programs & Services
          </h2>
        </motion.div>
        <p className="max-w-2xl text-white/55 text-lg mb-14">
          We train <span className="text-white">mobile & off-site</span> at
          approved parks, fields, schools, and facilities throughout{" "}
          <span className="text-white">Northeast Ohio</span> — Canton, Akron,
          Wooster, New Philadelphia, Alliance & surrounding communities.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-card-${i}`}
                className={`group flex flex-col p-8 border transition-all duration-500 hover:-translate-y-2 ${
                  s.featured
                    ? "bg-white text-black border-white"
                    : "bg-black border-white/10 hover:border-white/30"
                }`}
              >
                <div className="flex items-center justify-between mb-6">
                  <Icon size={28} strokeWidth={1.5} className={s.featured ? "text-black" : "text-white/80"} />
                  <span className={`font-mono-accent text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${s.featured ? "border-black/30 text-black/70" : "border-white/25 text-white/60"}`}>
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl leading-none mb-5">
                  {s.title}
                </h3>
                <ul className={`space-y-2 text-sm flex-1 ${s.featured ? "text-black/70" : "text-white/55"}`}>
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span className={s.featured ? "text-black" : "text-white/40"}>—</span>
                      {p}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => scrollTo(s.target)}
                  className={`mt-8 inline-flex items-center justify-between gap-2 uppercase tracking-[0.15em] font-bold text-xs px-5 py-4 transition-colors duration-300 ${
                    s.featured
                      ? "bg-black text-white hover:bg-black/80"
                      : "border border-white/20 text-white hover:bg-white hover:text-black"
                  }`}
                >
                  {s.cta}
                  <ArrowUpRight size={16} />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
